import { useSelector } from 'react-redux';
import useAuth from './useAuth';
import { CLIENT_CYNERGY_ID, PHYSICIAN_CLIENT, PATIENT_NAME_ORDERING } from 'constant';

// ==============================|| AUTH HOOKS ||============================== //

const usePermission = () => {
    const { user } = useAuth();

    const userPermissions = user?.permissions?.map((item) => item.code);

    const hasPermission = (permissions) => {
        if (permissions?.length > 0) {
            return userPermissions?.some((item) => permissions.includes(item));
        }
        return true;
    };

    const userRoles = user?.roles?.map((item) => item.role);

    const hasRole = (roles) => userRoles?.some((item) => roles.includes(item));

    const isNurse = hasRole(['Center Nurse', 'School Nurse']);

    const isTherapist = hasRole(['Therapist', 'Assistant Therapist']);

    const isPsychologist = hasRole(['Psychologist']);

    const isPsychologicalAssociate = hasRole(['Psychological Associate']);

    const isPsychiatrist = hasRole(['Psychiatrist']);

    const isAssistantTherapist = hasRole(['Assistant Therapist']);

    const isTherapistOnly = user?.roles?.[0]?.role === 'Therapist';

    const isPhysician = hasRole(['Physician']) || user?.client_id === PHYSICIAN_CLIENT;

    const isSuperAdmin = user?.is_superuser;

    const isSuperVisor = user?.roles?.[0]?.role === 'Therapist' && user?.supervisee?.length > 0;

    const isPCPSuperVisor = user?.roles?.[0]?.role === 'Personal Care Provider' && user?.supervisee?.length > 0;

    const isCareCoordinator = user?.roles?.[0]?.role === 'Care Coordinator';
    const isECDS = user?.roles?.[0]?.role === 'ECDS';

    const isCynergyClient = CLIENT_CYNERGY_ID === user?.client_id;

    const isMaster = user?.roles?.[0]?.role === 'Master Administrator';

    const isPersonalCareProvider = user?.roles?.[0]?.role === 'Personal Care Provider';

    const isPersonalCareAide = user?.roles?.[0]?.role === 'Personal Care Aide';

    const isSameAsMasterAdmin = user?.is_same_as_masteradmin;

    const isTeacher = user?.roles?.[0]?.role === 'Center Teacher';

    const hasOwenerPermission = (ownerId) => user?.emp_id === ownerId;

    const isServiceAvailable = (service) =>
        service ? user?.services?.map((val) => val?.service_code)?.includes(service?.toUpperCase()) : false;

    const hasServiceAvailable = (service) => {
        if (typeof service === 'object' && service?.length > 0) {
            return service?.some((item) => user?.services?.map((val) => val?.service_code)?.includes(item?.toUpperCase()));
        }
        if (typeof service === 'string') {
            return service ? user?.services?.map((val) => val?.service_code)?.includes(service?.toUpperCase()) : false;
        }
        return false;
    };

    const patientNameOrder = PATIENT_NAME_ORDERING.find((item) => item.value === user?.patient_name_ordering);

    return {
        userPermissions,
        hasPermission,
        userRoles,
        hasRole,
        isNurse,
        isTherapist,
        isTherapistOnly,
        isSuperVisor,
        isPCPSuperVisor,
        isECDS,
        isPhysician,
        isSuperAdmin,
        isCynergyClient,
        isMaster,
        isSameAsMasterAdmin,
        isPersonalCareProvider,
        isPersonalCareAide,
        isTeacher,
        isAssistantTherapist,
        isCareCoordinator,
        hasOwenerPermission,
        isServiceAvailable,
        hasServiceAvailable,
        isPsychologist,
        isPsychologicalAssociate,
        isPsychiatrist,
        patientNameOrder
    };
};

export default usePermission;
